// Synthesised audio for game beats. No asset files — Web Audio API only.
// Respects prefers-reduced-motion and a localStorage mute flag.

let ctx: AudioContext | undefined;
let muted = false;

try {
  muted = localStorage.getItem("breakpoint.muted") === "1";
} catch {
  /* SSR-safe */
}

function ensureCtx(): AudioContext | undefined {
  if (typeof window === "undefined") return undefined;
  if (muted) return undefined;
  try {
    ctx = ctx ?? new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return undefined;
  }
}

export function setMuted(m: boolean): void {
  muted = m;
  try { localStorage.setItem("breakpoint.muted", m ? "1" : "0"); } catch { /* ignore */ }
}
export function isMuted(): boolean { return muted; }

// 1. Dice clack — short noise burst then settling tone.
export function playDice(): void {
  const c = ensureCtx();
  if (!c) return;
  const noiseBuf = c.createBuffer(1, c.sampleRate * 0.06, c.sampleRate);
  const data = noiseBuf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = c.createBufferSource();
  src.buffer = noiseBuf;
  const filt = c.createBiquadFilter();
  filt.type = "highpass";
  filt.frequency.value = 1200;
  const g = c.createGain();
  g.gain.setValueAtTime(0.18, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.12);
  src.connect(filt).connect(g).connect(c.destination);
  src.start();
  src.stop(c.currentTime + 0.12);
}

// 2. Success — ascending two-tone bell.
export function playSuccess(): void {
  const c = ensureCtx();
  if (!c) return;
  const tones = [660, 880];
  tones.forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const g = c.createGain();
    const t0 = c.currentTime + i * 0.08;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.14, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.34);
  });
}

// 3. Reveal sting — deeper, longer than success, used when a stage card is uncovered.
export function playReveal(): void {
  const c = ensureCtx();
  if (!c) return;
  const tones = [330, 440, 660];
  tones.forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const g = c.createGain();
    const t0 = c.currentTime + i * 0.09;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.62);
  });
}

// 4. Fail — short descending thud.
export function playFail(): void {
  const c = ensureCtx();
  if (!c) return;
  const osc = c.createOscillator();
  osc.type = "square";
  osc.frequency.setValueAtTime(220, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(80, c.currentTime + 0.18);
  const g = c.createGain();
  g.gain.setValueAtTime(0.1, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.22);
  osc.connect(g).connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + 0.22);
}

// 5. Inject klaxon — pulsing alert, two beeps.
export function playInject(): void {
  const c = ensureCtx();
  if (!c) return;
  for (let i = 0; i < 2; i++) {
    const osc = c.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = 740 - i * 60;
    const g = c.createGain();
    const t0 = c.currentTime + i * 0.18;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.12, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.18);
  }
}

// 6. Win fanfare — three rising notes for the celebration.
export function playWin(): void {
  const c = ensureCtx();
  if (!c) return;
  [523, 659, 784, 1047].forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const g = c.createGain();
    const t0 = c.currentTime + i * 0.12;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.16, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.52);
  });
}

// 7. Lose — long minor descent.
export function playLose(): void {
  const c = ensureCtx();
  if (!c) return;
  [440, 415, 392, 349].forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const g = c.createGain();
    const t0 = c.currentTime + i * 0.18;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.14, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.7);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.72);
  });
}
